import { memo, useState } from "react";
import Form from "../Form.js";
import { useSnackbar } from "../../utils/index.js"
import Spinner from "../Spinner.js"
import {
    createAnimal,
    updateAnimal,
    deleteAnimal,
    getAnimal,
    claimExternalSaleAnimal,
} from "../../api/index.js";
import {
    IconButton,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AreYouSurePopup from "../popups/AreYouSurePopup.js";
import { dayjs } from "../../utils/index.js";

const AnimalForm = ({
    datePickerWidth = "595px",
    isCreate, // Declares if the form is used for creation (true) or update (false)
    fetchedOwner = {}, // Used in update ~ the owner to be updated object
    setFetchedOwner = {}, // Used in update ~ the set method of the owner to be updated 
    ownerId = {}, // Used in update ~ the id of the onwer to be updated
    setIsAnimalSelected, // Used in update ~ declares if owner is selected to be updated
    isFromList, // Declares if the form is rendered in list page or not 
    setIsCreateOpen = () => console.log(""), // Used when isFromList is true ~ to set the open status of the modal
    fetchListData = () => console.log(""), // Used when isFromList is true ~ to update the table
    flockCode,
    fetchedAnimal,
    setFetchedAnimal,
    ownersDropdownValues,
    initialExternalSaleAnimal = null,
}
) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [externalSaleAnimal, setExternalSaleAnimal] = useState(initialExternalSaleAnimal);
    const [declarationAccepted, setDeclarationAccepted] = useState(false);

    const prefilledAnimal = externalSaleAnimal?.animal;
    const generatedRegistrationCode = flockCode.length > 4 ? flockCode : `${fetchedOwner?.code}${flockCode}`;

    const { success, error } = useSnackbar();

    const createFormContent = [
        {
            customType: "input",
            id: "registrationCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου",
            required: true,
            value: prefilledAnimal?.registrationCode || (isFromList ? null : generatedRegistrationCode),
            disabled: Boolean(prefilledAnimal) || !isFromList ? true : false,
        },
        {
            customType: "dropdown",
            id: "sex",
            items: [
                { value: "Άρρεν", text: "Άρρεν" },
                { value: "Θήλυ", text: "Θήλυ" },
            ],
            placeholder: "Φύλο",
            required: true,
            defaultValue: prefilledAnimal?.sex,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "date-picker",
            id: "birthDate",
            type: "desktop",
            label: "Ημ/νία Γέννησης",
            width: datePickerWidth,
            background: "third",
            value: prefilledAnimal ? dayjs(prefilledAnimal.birthDate) : null,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "date-picker",
            id: "registrationDate",
            type: "desktop",
            label: "Ημ/νία Εγγραφής",
            width: datePickerWidth,
            background: "third",
            value: prefilledAnimal ? dayjs(prefilledAnimal.registrationDate) : dayjs(new Date().toLocaleDateString("el-GR")),
            disabled: Boolean(prefilledAnimal),
            required: true,
        },
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            defaultValue: `${fetchedOwner?.code} - ${fetchedOwner?.lastName} ${fetchedOwner?.firstName}`,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "input",
            id: "flockCode",
            type: "text",
            placeholder: "Κωδικός στην Εκτροφή",
            required: true,
            value: prefilledAnimal?.flockCode || (isFromList ? null : (flockCode.length > 4 ? flockCode.substr(flockCode.length - 4) : flockCode)),
            disabled: Boolean(prefilledAnimal) || !isFromList ? true : false
        },
        {
            customType: "dropdown",
            id: "breedCode",
            items: [
                { value: "Χίος", text: "Χίος" },
                { value: "Σέρρες", text: "Σέρρες" },
                { value: "Αίγες", text: "Αίγες" },
                { value: "Zaanen", text: "Zaanen" },
                { value: "Δαμασκού", text: "Δαμασκού" },
            ],
            placeholder: "Φυλή",
            defaultValue: prefilledAnimal?.breedCode,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "input",
            id: "sireCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου Πατέρα",
            value: prefilledAnimal?.sireCode,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "input",
            id: "damCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου Μητέρας",
            value: prefilledAnimal?.damCode,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "dropdown",
            id: "cullingCode",
            items: [
                { value: "Καμία", text: "Καμία" },
                { value: "Σφαγή", text: "Σφαγή" },
                { value: "Πώληση Αναπαραγ", text: "Πώληση Αναπαραγ" },
                { value: "Μεγάλη Ηλικία", text: "Μεγάλη Ηλικία" },
                { value: "Θάνατος", text: "Θάνατος" },
                { value: "Διάφοροι Λόγοι", text: "Διάφοροι Λόγοι" },
                { value: "Πάθηση Αναπαραγ", text: "Πάθηση Αναπαραγ" },
                { value: "Πάθηση Μαστού", text: "Πάθηση Μαστού" },
                { value: "Πάθηση", text: "Πάθηση" },
                { value: "Αποβολή", text: "Αποβολή" },
                { value: "Άγνωστη Αιτία", text: "Άγνωστη Αιτία" },
            ],
            placeholder: "Αιτία Απομάκρυνσης",
            defaultValue: prefilledAnimal?.cullingCode,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "date-picker",
            id: "cullingDate",
            type: "desktop",
            label: "Ημ/νία Απομάκρυνσης",
            width: datePickerWidth,
            background: "third",
            value: prefilledAnimal?.cullingDate ? dayjs(prefilledAnimal.cullingDate) : null,
            disabled: Boolean(prefilledAnimal),
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
            disabled: Boolean(prefilledAnimal),
        },
    ]

    const selectFormContent = [
        {
            customType: "input",
            id: "registrationCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου",
            required: true,
            value: fetchedAnimal?.registrationCode,
            disabled: true
        },
        {
            customType: "dropdown",
            id: "sex",
            items: [
                { value: "Άρρεν", text: "Άρρεν" },
                { value: "Θήλυ", text: "Θήλυ" },
            ],
            placeholder: "Φύλο",
            required: true,
            defaultValue: fetchedAnimal?.sex,
        },
        {
            customType: "date-picker",
            id: "birthDate",
            type: "desktop",
            label: "Ημ/νία Γέννησης",
            width: datePickerWidth,
            background: "third",
            value: dayjs(fetchedAnimal?.birthDate),
        },
        {
            customType: "date-picker",
            id: "registrationDate",
            type: "desktop",
            label: "Ημ/νία Εγγραφής",
            width: datePickerWidth,
            background: "third",
            value: dayjs(fetchedAnimal?.registrationDate),
            required: true,
        },
        // {
        //     customType: "dropdown",
        //     id: "owner",
        //     items: ownersDropdownValues,
        //     defaultValue: `${fetchedOwner?.code} - ${fetchedOwner?.lastName} ${fetchedOwner?.firstName}`,
        //     placeholder: "Εκτροφή",
        //     required: true,
        // },
        {
            customType: "input",
            id: "flockCode",
            type: "text",
            placeholder: "Κωδικός στην Εκτροφή",
            required: true,
            value: fetchedAnimal?.flockCode,
            disabled: true,
        },
        {
            customType: "dropdown",
            id: "breedCode",
            items: [
                { value: "Χίος", text: "Χίος" },
                { value: "Σέρρες", text: "Σέρρες" },
                { value: "Αίγες", text: "Αίγες" },
                { value: "Zaanen", text: "Zaanen" },
                { value: "Δαμασκού", text: "Δαμασκού" },
            ],
            placeholder: "Φυλή",
            defaultValue: fetchedAnimal?.breedCode,
            required: true,
        },
        {
            customType: "input",
            id: "sireCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου Πατέρα",
            value: fetchedAnimal?.sireCode,
        },
        {
            customType: "input",
            id: "damCode",
            type: "text",
            placeholder: "Κωδ. Γενεαλογικού Βιβλίου Μητέρας",
            value: fetchedAnimal?.damCode,
        },
        {
            customType: "dropdown",
            id: "cullingCode",
            items: [
                { value: "Καμία", text: "Καμία" },
                { value: "Σφαγή", text: "Σφαγή" },
                { value: "Πώληση Αναπαραγ", text: "Πώληση Αναπαραγ" },
                { value: "Μεγάλη Ηλικία", text: "Μεγάλη Ηλικία" },
                { value: "Θάνατος", text: "Θάνατος" },
                { value: "Διάφοροι Λόγοι", text: "Διάφοροι Λόγοι" },
                { value: "Πάθηση Αναπαραγ", text: "Πάθηση Αναπαραγ" },
                { value: "Πάθηση Μαστού", text: "Πάθηση Μαστού" },
                { value: "Πάθηση", text: "Πάθηση" },
                { value: "Αποβολή", text: "Αποβολή" },
                { value: "Άγνωστη Αιτία", text: "Άγνωστη Αιτία" },
            ],
            placeholder: "Αιτία Απομάκρυνσης",
            defaultValue: fetchedAnimal?.cullingCode,
        },
        {
            customType: "date-picker",
            id: "cullingDate",
            type: "desktop",
            label: "Ημ/νία Απομάκρυνσης",
            width: datePickerWidth,
            background: "third",
            value: dayjs(fetchedAnimal?.cullingDate),
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ]

    const handleFormSubmit = async (values) => {
        setIsLoading(true)
        var valuesToSend = { ...values };
        valuesToSend.owner = ownerId;
        const { success: successCode, message, requiresExternalSaleConfirmation, existingAnimal } = await createAnimal(valuesToSend);
        if (successCode) {
            success(message);
            if (isFromList) {
                setIsCreateOpen(false);
                fetchListData(ownerId);
            } else {
                setIsAnimalSelected(false);
            }
        } else if (requiresExternalSaleConfirmation) {
            setExternalSaleAnimal(existingAnimal);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const closeExternalSaleDialog = () => {
        setExternalSaleAnimal(null);
        setDeclarationAccepted(false);
    };

    const handleExternalSaleClaim = async () => {
        if (!declarationAccepted) {
            error("Πρέπει να επιβεβαιώσετε ότι το ζώο υπάρχει στην εκτροφή");
            return;
        }

        setIsLoading(true);
        try {
            const { success: successCode, message } = await claimExternalSaleAnimal(
                externalSaleAnimal.animalId,
                ownerId,
                declarationAccepted,
            );
            if (successCode) {
                success(message);
                closeExternalSaleDialog();
                if (isFromList) {
                    setIsCreateOpen(false);
                    fetchListData(ownerId);
                } else {
                    setIsAnimalSelected(false);
                }
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    };

    const handleUpdateSubmit = async (values) => {
        setIsLoading(true);
        var valuesToSend = { ...values };
        valuesToSend.owner = fetchedOwner?._id;
        const { success: successCode, message } = await updateAnimal(fetchedAnimal._id, fetchedOwner?._id, valuesToSend);
        if (successCode) {
            success(message);

            // Re-fetch the updated animal data to refresh the form
            if (setFetchedAnimal) {
                const { success: fetchSuccess, animal } = await getAnimal(fetchedAnimal.registrationCode, fetchedOwner?._id);
                if (fetchSuccess && animal) {
                    setFetchedAnimal(animal);
                }
            }

            if (isFromList) {
                fetchListData(ownerId);
            }
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleDelete = async () => {
        setIsLoading(true);
        setIsPopupOpen(false);
        const { success: successCode, message } = await deleteAnimal(fetchedAnimal._id);
        if (successCode) {
            success(message);
            if (isFromList) {
                fetchListData(ownerId);
            } else {
                setIsAnimalSelected(false);
            }

        } else {
            error(message);
        }
        setIsCreateOpen(false);
        setFetchedOwner(null);
        setIsLoading(false);
    }

    return (
        <>
            <AreYouSurePopup
                width={"900px"}
                isOpen={isPopupOpen}
                title="Διαγραφή εκτροφής"
                titleColor="black"
                onClose={() => setIsPopupOpen(false)}
                innerText={`Είστε σίγουροι ότι θέλετε να διαγράψετε το ζώο με κωδικό Γενεαλογικού Βιβλίου "${fetchedAnimal?.registrationCode}";`}
                declineText="Πίσω"
                onDecline={() => setIsPopupOpen(false)}
                acceptText="Ναι, Διαγραφή Ζώου"
                onAccept={() => handleDelete()}
            />
            <Spinner open={isLoading} />
            {externalSaleAnimal && (
                <Box sx={{ width: "100%", maxWidth: "600px", mx: "auto", mb: 3, p: 2, border: 1, borderColor: "warning.main", borderRadius: 1 }}>
                    <Typography sx={{ mb: 1 }}>
                        Υπάρχει καταγεγραμμένη εξωτερική πώληση για το ζώο με κωδικό {externalSaleAnimal.registrationCode} (Παραγγελία #{externalSaleAnimal.orderNumber}). Τα στοιχεία του υπάρχοντος ζώου έχουν προσυμπληρωθεί παρακάτω.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={declarationAccepted}
                                onChange={(event) => setDeclarationAccepted(event.target.checked)}
                                required
                            />
                        }
                        label="Δηλώνω υπεύθυνα ότι το ζώο υπάρχει στην τρέχουσα εκτροφή."
                        sx={{ alignItems: "flex-start", mb: 1 }}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={closeExternalSaleDialog}>Ακύρωση</Button>
                        <Button variant="contained" onClick={handleExternalSaleClaim} disabled={!declarationAccepted}>
                            Προσθήκη υπάρχοντος ζώου
                        </Button>
                    </Box>
                </Box>
            )}
            <Form content={isCreate ? createFormContent : selectFormContent} onSubmit={isCreate ? handleFormSubmit : handleUpdateSubmit} toResetForm={false} />
            <Button
                onClick={() => setIsAnimalSelected(false)}
                sx={{
                    marginTop: "1rem",
                    padding: "0.5rem 1rem",
                    backgroundColor: "third",
                    color: "secondary.main",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    variant: "h5",
                }}
            >
                Πίσω
            </Button>
            {!isCreate ? (
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <IconButton
                        aria-label="delete"
                        onClick={() => setIsPopupOpen(true)}
                    >
                        <DeleteIcon />
                    </IconButton >
                </Box>
            ) : (
                <></>
            )}
        </>
    )
}

export default memo(AnimalForm)
